import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { TimeSeriesPoint } from "../../../api/ParsersPanel";
import { useUserState } from "../../../store/user/hooks";
import LinksLineChart from "./LinksLineChart";
import SourceStatusBadge from "./SourceStatusBadge";
import ReasonBadge from "./ReasonBadge";
import {
  SourceStatsRow,
  RejectionReasonRow,
} from "../../../api/ParsersPanel";
import { ParsersPanel } from "../../../api/ParsersPanel";

interface Props {
  source: SourceStatsRow;
  node: "tamrur" | "arabic";
  onClose: () => void;
  /**
   * Fetcher returning the daily time series for the given source link.
   * Lets the drawer be reused across different source types
   * (Tamrur, Arabic, …) without leaking endpoint knowledge.
   */
  fetchTimeseries: (
    token: string,
    sourceLink: string,
    days: number,
  ) => Promise<TimeSeriesPoint[]>;
}

const SourceDetailDrawer: React.FC<Props> = ({ source, node, onClose, fetchTimeseries }) => {
  const { token } = useUserState();
  const [series, setSeries] = useState<TimeSeriesPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [rejections, setRejections] = useState<RejectionReasonRow[]>([]);

  useEffect(() => {
    if (!token || !source.source_link) return;
    setLoading(true);
    fetchTimeseries(token, source.source_link, 90)
      .then((points) => {
        setSeries(points);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [token, source.source_link, fetchTimeseries]);

  useEffect(() => {
    if (!token || !source.source_link) return;

    ParsersPanel.getRejectionStats(token, node)
      .then((data) => {
        const matching = data.rows
          .filter((row) => row.source_link === source.source_link)
          .sort((a, b) => b.count - a.count);

        setRejections(matching);
      })
      .catch(() => setRejections([]));
  }, [token, node, source.source_link]);

  return (
    <Overlay onClick={onClose}>
      <Drawer onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>Source Detail</Title>
          <CloseBtn onClick={onClose}>✕</CloseBtn>
        </Header>

        <SourceUrl href={source.source_link ?? "#"} target="_blank" rel="noreferrer">
          {source.source_link ?? "-"}
        </SourceUrl>

        <BadgeRow>
          <SourceStatusBadge status={source.status} />
          {source.drop_pct !== null && (
            <DropLabel $dropping={source.drop_pct > 0}>
              {source.drop_pct > 0 ? "▼" : "▲"} {Math.abs(source.drop_pct)}%
            </DropLabel>
          )}
        </BadgeRow>

        <StatsGrid>
          <Stat label="Last Collection" value={source.last_collection_at ?? "-"} />
          <Stat label="Last 7 Days" value={source.count_last_week} />
          <Stat label="Prev 7 Days" value={source.count_prev_week} />
          <Stat label="Past 3 Months" value={source.count_3mo} />
        </StatsGrid>

        <SectionTitle>Why nothing was saved (last 30 days)</SectionTitle>

        {rejections.length === 0 ? (
          <LoadingMsg>No rejections in this window.</LoadingMsg>
        ) : (
          rejections.map((row, i) => (
            <RejectionRow key={`${row.reason}-${i}`}>
              <ReasonBadge
                reason={row.reason}
                disposition={row.disposition}
                label={row.reason_label}
              />
              <span>×{row.count}</span>
            </RejectionRow>
          ))
        )}

        <SectionTitle>90-Day Collection History</SectionTitle>
        {loading ? (
          <LoadingMsg>Loading chart…</LoadingMsg>
        ) : series.length === 0 ? (
          <LoadingMsg>No data available.</LoadingMsg>
        ) : (
          <LinksLineChart data={series} height={220} />
        )}
      </Drawer>
    </Overlay>
  );
};

const Stat: React.FC<{ label: string; value: string | number }> = ({
  label,
  value,
}) => (
  <StatBox>
    <StatLabel>{label}</StatLabel>
    <StatValue>{value}</StatValue>
  </StatBox>
);

export default SourceDetailDrawer;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 1000;
  display: flex;
  justify-content: flex-end;
`;

const Drawer = styled.div`
  width: 520px;
  max-width: 95vw;
  background: #fff;
  height: 100%;
  overflow-y: auto;
  padding: 28px 28px 40px;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: 768px) {
    width: 100vw;
    max-width: 100vw;
    padding: 20px 16px 32px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h2`
  font-size: 18px;
  color: #0058A9;
  margin: 0;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: #666;
  &:hover { color: #222; }
`;

const SourceUrl = styled.a`
  font-size: 13px;
  color: #0058A9;
  word-break: break-all;
  text-decoration: underline;
`;

const BadgeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const RejectionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #555;
`;

const DropLabel = styled.span<{ $dropping: boolean }>`
  font-size: 13px;
  font-weight: 700;
  color: ${({ $dropping }) => ($dropping ? "#e65100" : "#2e7d32")};
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 360px) {
    grid-template-columns: 1fr;
  }
`;

const StatBox = styled.div`
  background: #f6f9ff;
  border-radius: 6px;
  padding: 12px 14px;
`;

const StatLabel = styled.p`
  font-size: 11px;
  color: #888;
  margin: 0 0 4px;
  text-transform: uppercase;
`;

const StatValue = styled.p`
  font-size: 20px;
  font-weight: 700;
  color: #222;
  margin: 0;
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  color: #555;
  margin: 8px 0 0;
`;

const LoadingMsg = styled.p`
  color: #aaa;
  font-size: 13px;
`;
